import { useId, type ReactNode } from 'react';
import { Spinner } from '../../components/ui/Skeleton';
import { formatPoints } from '../../lib/format';
import {
  formatSprintRange,
  SPRINT_STATE_LABEL,
  type BacklogSection,
  type CountScope,
  type SectionTotals,
} from './sprint-sections';
import { SprintProgressMeter } from './SprintProgressMeter';
import { sprintProgress } from './sprint-progress';

/**
 * One collapsible sprint section, header first.
 *
 * Or's board, shot 1:  `APFS v1.0.33 · 14 Jul – 14 Aug (26 work items)` — plus the point sum, which
 * is the number a sprint is actually planned by.
 *
 * 🔴 MOBILE FIRST HERE, NOT AS AN AFTERTHOUGHT. At 390px the header is a FULL-WIDTH BUTTON two lines
 * tall: name and state on line 1, dates and totals on line 2 in smaller type. ⛔ Not one line that
 * shrinks — a 44px strip holding five ellipsised fragments is unreadable and, worse, hard to hit.
 * The whole header is the tap target, not the chevron.
 *
 * ⚠️ A number is never printed without its scope. `null` renders `—` (we have not been told), a floor
 * renders `26+`, and a whole-sprint number under an active filter says so on hover and carries a
 * marker. ⛔ `0` is reserved for "there are none", which is a different claim from all three.
 */

const STATE_CLASS: Record<string, string> = {
  active: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  planned: 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300',
  closed:
    'border-[color:var(--color-line)] bg-[color:var(--color-surface-sunken)] text-[color:var(--color-ink-muted)]',
};

export function SprintSection({
  section,
  open,
  onToggle,
  totals,
  filtered,
  busy,
  action,
  children,
}: {
  section: BacklogSection;
  open: boolean;
  onToggle: () => void;
  totals: SectionTotals;
  /**
   * PH-37. ⭐ Only used to SCOPE the progress split: the API's buckets count the whole sprint and know
   * nothing about the status/assignee filter, so under a filter they are marked `* whole sprint` —
   * the same honesty `totals` already applies to its own two numbers.
   */
  filtered: boolean;
  busy: boolean;
  /**
   * The per-section actions — "Complete sprint" on the active one, and since PH-23 "Start sprint" on
   * a planned one and "Edit" on either.
   *
   * ⛔ THEY ARE SIBLINGS OF THE HEADER BUTTON, NEVER CHILDREN OF IT. The whole header is one big tap
   * target (that is the mobile decision above), and a `<button>` inside a `<button>` is invalid HTML
   * that React will warn about and browsers resolve inconsistently — a tap meant for the action
   * would sometimes collapse the section instead. ⇒ The sticky bar became a flex ROW: header first
   * and flexible, actions last and `shrink-0`.
   *
   * ⚠️ MORE THAN ONE IS NORMAL NOW, so the slot itself lays them out (`flex … gap-1.5`) rather than
   * each caller wrapping its own. At 390px two compact buttons and the truncating name coexist; a
   * caller that stacked them would make the sticky header twice as tall on the one screen where
   * vertical space is scarcest.
   */
  action?: ReactNode;
  children: ReactNode;
}) {
  const bodyId = useId();
  const sprint = section.sprint;
  const state = sprint?.state ?? null;

  /**
   * PH-37 — the done / in-progress / not-started split, or `null`.
   *
   * ⛔ `null` FOR THE UNASSIGNED `Backlog` SECTION, AND NOT BY OVERSIGHT. That section is not a
   *   sprint, so there is no server aggregate for it — the split would have to be computed from the
   *   rows this page holds, which are capped at 100 and filtered, and it would need a definition of
   *   "done" this client is not entitled to invent (see `sprint-progress.ts`). ⭐ "What is left in
   *   the backlog" is also not the question that was asked: everything in the backlog is left. A
   *   plausible-looking bar there would be a floor wearing an exact number's clothes.
   * ⛔ `null` ALSO when the API did not send the fields, or when they do not add up — see the four
   *   refusals in `sprintProgress`. In both cases this header renders exactly what it did before.
   */
  const progress = sprint ? sprintProgress(sprint, filtered) : null;

  return (
    // ⭐ Named so a reader — and a test — can address one section rather than the whole page.
    <section aria-label={section.name} className="border-b border-[color:var(--color-line)]">
      <div className="sticky top-0 z-10 flex items-start bg-[color:var(--color-surface-sunken)] sm:items-center">
        <h2 className="m-0 min-w-0 flex-1">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex w-full items-start gap-2 px-3 py-2.5 text-left hover:bg-black/[0.04] sm:items-center sm:px-4 sm:py-2 dark:hover:bg-white/[0.06]"
        >
          <svg
            viewBox="0 0 16 16"
            width="12"
            height="12"
            aria-hidden="true"
            className={`mt-1 shrink-0 text-[color:var(--color-ink-muted)] transition-transform duration-100 sm:mt-0 ${
              open ? 'rotate-90' : ''
            }`}
          >
            <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="2" />
          </svg>

          <span className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-2">
            <span className="flex min-w-0 items-center gap-2">
              <span
                className="truncate text-[13px] font-semibold text-[color:var(--color-ink)]"
                title={sprint?.goal ?? undefined}
              >
                {section.name}
              </span>
              {state ? (
                <span
                  className={`shrink-0 rounded border px-1.5 text-[10.5px] font-bold tracking-wide uppercase ${
                    STATE_CLASS[state] ?? STATE_CLASS.closed
                  }`}
                >
                  {SPRINT_STATE_LABEL[state]}
                </span>
              ) : null}
            </span>

            <span className="min-w-0 truncate text-[11.5px] text-[color:var(--color-ink-muted)]">
              {sprint ? (
                <>
                  {formatSprintRange(sprint.starts_on, sprint.ends_on)}
                  <Dot />
                </>
              ) : null}
              <Count totals={totals} />
              <Dot />
              <Points totals={totals} />
              {busy ? (
                <>
                  {' '}
                  <Spinner label="Loading" />
                </>
              ) : null}
            </span>

            {/* ⭐ A THIRD LINE, full width. `basis-full` forces the wrap even inside the `sm:flex-row`
                that puts the name and the meta line side by side above `sm` — the meter is the one
                fragment that must never share a line, because a 40px track is decoration. */}
            {progress ? (
              <span className="flex w-full min-w-0 basis-full">
                <SprintProgressMeter progress={progress} />
              </span>
            ) : null}
          </span>
        </button>
        </h2>

        {/* ⚠️ `self-center` and `shrink-0`: at 390px the header is two lines tall and the actions must
            sit against the middle of them without being squeezed to an ellipsis. The NAME truncates
            instead — it is the thing a reader can still identify from its first few words. */}
        {action ? (
          <div className="flex shrink-0 items-center gap-1.5 self-center pr-3 pl-1 sm:pr-4">
            {action}
          </div>
        ) : null}
      </div>

      {/* ⭐ The body is not rendered while collapsed — hundreds of hidden rows in the DOM is what makes
          a list like this feel slow, which is the same reason pagination is server-side. */}
      <div id={bodyId} hidden={!open}>
        {open ? children : null}
      </div>
    </section>
  );
}

function Dot() {
  return <span aria-hidden="true"> · </span>;
}

const SCOPE_TITLE: Record<CountScope, string> = {
  exact: 'Counted under the filters currently on screen',
  // ⚠️ REWORDED, not removed. `floor` no longer means "the API cannot be asked for tickets with no
  //    sprint" — it can, and that count is exact now. What still produces a floor is the 100-row page
  //    cap, which leaves a POINT SUM covering only the rows on screen.
  floor: 'At least this much — the API returns at most 100 rows per request, so this covers only the rows currently loaded.',
  'whole-sprint':
    'This counts the WHOLE sprint, not the filtered view — it comes from the sprint itself, which knows nothing about the status or assignee filter. Open the section to see the filtered count.',
};

function suffixFor(scope: CountScope): string {
  if (scope === 'floor') return '+';
  // ⭐ A marker, not only a tooltip: a number whose caveat is hover-only is a caveat nobody reads,
  //   and hover does not exist on the phone Or reviews this on.
  if (scope === 'whole-sprint') return '*';
  return '';
}

function Count({ totals }: { totals: SectionTotals }) {
  if (totals.count === null) {
    return <span title="This section's size is not known — its request has not answered.">— work items</span>;
  }

  const suffix = suffixFor(totals.countScope);
  const plural = totals.count === 1 && totals.countScope === 'exact' ? '' : 's';

  return (
    <span title={SCOPE_TITLE[totals.countScope]}>
      {totals.count}
      {suffix} work item{plural}
      {totals.countScope === 'whole-sprint' ? (
        <span className="ml-1 italic">(whole sprint)</span>
      ) : null}
    </span>
  );
}

function Points({ totals }: { totals: SectionTotals }) {
  if (totals.points === null) return <span>— pts</span>;
  return (
    <span title={SCOPE_TITLE[totals.pointsScope]}>
      {formatPoints(totals.points) ?? '0'}
      {suffixFor(totals.pointsScope)} pts
    </span>
  );
}
